import type { EconomyBuilding } from "./economy-types.js";
import type { TroopComposition } from "./logistics-types.js";
import type { ResearchEffects } from "./research-types.js";

export const INFRASTRUCTURE_TARGETS = [
  "mining-rig",
  "power-generator",
  "construction-yard",
  "rig-factory",
  "yard-factory",
  "factory-works",
  "robot-factory",
  "research-lab",
  "shield-generator",
  "supply-depot",
  "headquarters",
] as const;

export const INFRASTRUCTURE_FALLBACK = [
  "shield-generator",
  "supply-depot",
  "robot-factory",
  "factory-works",
  "yard-factory",
  "rig-factory",
  "construction-yard",
  "mining-rig",
  "research-lab",
  "power-generator",
  "headquarters",
] as const;

export type InfrastructureTarget = (typeof INFRASTRUCTURE_TARGETS)[number];

export interface CombatReport {
  readonly effectiveTarget: InfrastructureTarget;
  readonly shieldAbsorbed: string;
  readonly troopDamage: string;
  readonly infrastructureDamage: string;
  readonly blastDoorsActive: boolean;
}

export interface CombatDamageReport extends CombatReport {
  readonly attackerId: string;
  readonly targetId: string;
}

export interface CombatBorderState {
  readonly shieldCharge: string;
  readonly requestedTarget: InfrastructureTarget;
  readonly effectiveTarget: InfrastructureTarget;
  readonly lastAttack: CombatReport | null;
}

export interface CombatState {
  readonly borders: Readonly<Record<string, CombatBorderState>>;
}

export interface CombatSeatInput {
  readonly id: string;
  readonly headquarters: string;
  readonly buildings: Readonly<Record<EconomyBuilding, string>>;
  readonly wreckage: Readonly<Record<EconomyBuilding, string>>;
  readonly borders: Readonly<
    Record<
      string,
      {
        readonly troops: TroopComposition;
        readonly standingOrder: "hold" | "assault" | "raid";
        readonly shieldCharge: string;
        readonly requestedTarget: InfrastructureTarget;
        readonly supplyEffectiveness: string;
      }
    >
  >;
  readonly operatedShieldGenerators: string;
  readonly researchEffects: ResearchEffects;
}

export interface CombatSeatResult {
  readonly id: string;
  readonly headquarters: string;
  readonly buildings: Readonly<Record<EconomyBuilding, string>>;
  readonly wreckage: Readonly<Record<EconomyBuilding, string>>;
  readonly borders: Readonly<
    Record<
      string,
      {
        readonly troops: TroopComposition;
        readonly shieldCharge: string;
        readonly effectiveTarget: InfrastructureTarget;
        readonly lastAttack: CombatReport | null;
      }
    >
  >;
}

export interface CombatResult {
  readonly seats: readonly CombatSeatResult[];
  readonly breached: readonly { readonly seatId: string; readonly borderId: string }[];
  readonly reports: readonly CombatDamageReport[];
}
